import React from "react";
import { Link } from "react-router-dom";
import { Drawer } from "antd";
import { ArrowRightOutlined, CloseOutlined } from "@ant-design/icons";
import { StatusTag } from "../components/StatusTag";
import { BudgetLine, Tone, usd } from "../model/types";
import { projectUrl } from "../model/projects";
import { formatCompact, formatMoney } from "../model/format";
import classes from "./PlaybookDrawer.module.less";

const scoreTone = (score: number): Tone =>
    score >= 78 ? "green" : score >= 62 ? "amber" : "red";

/** Right-hand drawer summarizing the renewal negotiation playbook. */
export const PlaybookDrawer: React.FC<{
    line: BudgetLine | null;
    onClose: () => void;
}> = ({ line, onClose }) => {
    const pb = line?.playbook;
    return (
        <Drawer
            open={Boolean(line && pb)}
            onClose={onClose}
            placement="right"
            width={460}
            closable={false}
            title={
                line ? (
                    <div className={classes.titleWrap}>
                        {line.logoUrl && (
                            <img
                                className={classes.drawerLogo}
                                src={line.logoUrl}
                                alt=""
                            />
                        )}
                        <div>
                            <div className={classes.drawerName}>{line.label}</div>
                            <div className={classes.drawerSubtitle}>
                                Negotiation Playbook
                            </div>
                        </div>
                    </div>
                ) : (
                    ""
                )
            }
            extra={
                <div className={classes.headerActions}>
                    {line?.projectId !== undefined && (
                        <Link
                            className={classes.openProjectBtn}
                            to={projectUrl(line.projectId)}
                        >
                            Open project
                            <ArrowRightOutlined />
                        </Link>
                    )}
                    <button
                        type="button"
                        className={classes.closeBtn}
                        onClick={onClose}
                        aria-label="Close"
                    >
                        <CloseOutlined />
                    </button>
                </div>
            }
            bodyStyle={{ background: "#fafafa" }}
            headerStyle={{ padding: 16 }}
        >
            {line && pb && (
                <div className={classes.stack}>
                    <Box title="Contract">
                        <Field label="TCV" value={formatMoney(pb.tcv)} />
                        {line.currentSpend !== undefined && (
                            <Field
                                label="Annual"
                                value={formatMoney(usd(line.currentSpend))}
                            />
                        )}
                        <Field label="Term" value={pb.termLabel} />
                        <Field label="Payment terms" value={pb.paymentTermsLabel} />
                        <Field label="Auto-renewal" value={pb.autoRenew ? "Yes" : "No"} />
                        <Field label="Notice" value={`${pb.noticeDays} days`} />
                    </Box>

                    <Box
                        title="Score"
                        headerRight={
                            <StatusTag tone={scoreTone(pb.leverageScore)}>
                                {pb.leverageScore}/100
                            </StatusTag>
                        }
                    >
                        <div className={classes.subhead}>Contractual priorities</div>
                        <ul className={classes.priorities}>
                            {pb.priorities.map((p) => (
                                <li key={p}>{p}</li>
                            ))}
                        </ul>
                    </Box>

                    <Box
                        title="Identified savings"
                        headerRight={
                            <span className={classes.range}>
                                {formatCompact(pb.savingsLow)}–{formatCompact(pb.savingsHigh)}
                            </span>
                        }
                    >
                        <div className={classes.expected}>
                            <span className={classes.expectedLabel}>Expected savings</span>
                            <span className={classes.expectedValue}>
                                {formatMoney(pb.savingsExpected)} / yr
                            </span>
                        </div>
                        <div className={classes.strategies}>
                            {pb.strategies.map((s) => (
                                <div key={s.name} className={classes.strategy}>
                                    <div className={classes.strategyMain}>
                                        <div className={classes.strategyName}>{s.name}</div>
                                        <div className={classes.strategyDetail}>{s.detail}</div>
                                    </div>
                                    <div className={classes.strategySavings}>
                                        {formatCompact(s.savings)}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </Box>
                </div>
            )}
        </Drawer>
    );
};

const Box: React.FC<{
    title: string;
    headerRight?: React.ReactNode;
    children: React.ReactNode;
}> = ({ title, headerRight, children }) => (
    <div className={classes.box}>
        <div className={classes.boxTitle}>
            <span>{title}</span>
            {headerRight}
        </div>
        <div className={classes.boxBody}>{children}</div>
    </div>
);

const Field: React.FC<{ label: string; value: string }> = ({ label, value }) => (
    <div className={classes.field}>
        <span className={classes.fieldLabel}>{label}</span>
        <span className={classes.fieldValue}>{value}</span>
    </div>
);
